import { buildCollection, buildProperty } from "@firecms/core";
import { HomeButton } from "../components/HomeButton";
import { PromoCodeField } from "../components/PromoCodeField";

export type PromoCode = {
    code: string;
    description?: string;
    discount_type: string;
    discount_value: number;
    applicable_to: string[];
    max_uses?: number;
    uses_count: number;
    max_uses_per_user: number;
    valid_from: Date;
    valid_until?: Date;
    is_active: boolean;
    created_at: Date;
    created_by: string;
};

export const promoCodesCollection = buildCollection<PromoCode>({
    id: "promo_codes",
    name: "Promo Codes",
    singularName: "Promo Code",
    path: "promo_codes",
    icon: "LocalOffer",
    group: "Promotions",
    Actions: HomeButton,
    description: "Discount codes for subscriptions and booster packs",
    permissions: {
        edit: true,
        create: true,
        delete: true,
    },
    properties: {
        code: buildProperty({
            name: "Code",
            dataType: "string",
            description: "Type a code manually or click 🎲 to auto-generate one",
            Field: PromoCodeField,
            validation: {
                required: true,
                uppercase: true,
                trim: true,
            },
        }),
        description: buildProperty({
            name: "Description",
            dataType: "string",
            description: "Internal note about this promo",
        }),
        discount_type: buildProperty({
            name: "Discount Type",
            dataType: "string",
            enumValues: {
                percent: "Percentage",
                flat: "Flat Amount (Stars)",
            },
            validation: { required: true },
        }),
        discount_value: buildProperty({
            name: "Discount Value",
            dataType: "number",
            description: "Percentage (0-100) or flat stars amount",
            validation: { required: true, min: 1 },
        }),
        applicable_to: buildProperty({
            name: "Applicable To",
            dataType: "array",
            of: {
                dataType: "string",
                enumValues: {
                    pro_monthly: "Pro Monthly Subscription",
                    pro_yearly: "Pro Yearly Subscription",
                    starter: "Starter Pack",
                    study_session: "Study Session Pack",
                    power_pack: "Power Pack",
                    weekly_pass: "Weekly Pass",
                },
            },
            description: "Which items this code can be used for",
            validation: { required: true },
        }),
        max_uses: buildProperty({
            name: "Max Total Uses",
            dataType: "number",
            description: "Leave empty for unlimited",
        }),
        uses_count: buildProperty({
            name: "Times Used",
            dataType: "number",
            readOnly: true,
            defaultValue: 0,
        }),
        max_uses_per_user: buildProperty({
            name: "Max Uses Per User",
            dataType: "number",
            defaultValue: 1,
            validation: { required: true, min: 1 },
        }),
        valid_from: buildProperty({
            name: "Valid From",
            dataType: "date",
            mode: "date_time",
            autoValue: "on_create",
        }),
        valid_until: buildProperty({
            name: "Valid Until",
            dataType: "date",
            mode: "date_time",
            description: "Leave empty for no expiry",
        }),
        is_active: buildProperty({
            name: "Active",
            dataType: "boolean",
            defaultValue: true,
            columnWidth: 100,
        }),
        created_at: buildProperty({
            name: "Created At",
            dataType: "date",
            mode: "date_time",
            autoValue: "on_create",
            readOnly: true,
        }),
        created_by: buildProperty({
            name: "Created By",
            dataType: "string",
            readOnly: true,
        }),
    },
    propertiesOrder: [
        "is_active",
        "code",
        "description",
        "discount_type",
        "discount_value",
        "applicable_to",
        "max_uses",
        "uses_count",
        "valid_from",
        "valid_until",
    ],
});
